import type { RaContacts } from '../../ra-training/types';
import { normalizeMissionPartnerGuidanceCard } from './missionPartnerGuidance';
/**
 * TypeScript types for the /api/home-chat endpoints.
 * Re-exports shared chat primitives from FarChat/types and adds
 * session-list / thread shapes specific to the homepage multi-chat surface.
 *
 * See issues/20260429-homepage-multi-chat_spec.md §5.1 for the route table
 * and the authoritative JSON contract for each shape below.
 */

export type {
  ChatMessage,
  Citation,
  Attachment,
  TurnResponse,
  Turn,
  ClarificationEnvelope,
  TopicClarificationEnvelope,
  FarRegime,
} from '../FarChat/types';

export type GpcChatBinding = {
  gpc_chat_session_id: string;
  flow_id: string;
  bound_at: string;
};

/** Opaque snapshot used to avoid repeating an unchanged leave confirmation. */
export type WorkflowNavigationCheckpoint = Readonly<{
  editRevision: number;
}>;

export type WorkflowChatSessionChangeOptions = {
  /** A successful companion-route response has already saved this binding. */
  bindingManagedByServer?: boolean;
};

export type MrChatBinding = {
  mr_chat_session_id: string;
  flow_id: string;
  bound_at: string;
};

/** Owner-authorized construction-SOW chat binding. The browser never derives a flow from it. */
export type ConstructionSowChatBinding = {
  construction_sow_chat_session_id: string;
  flow_id: string;
  bound_at: string;
};

/**
 * Server-issued classification for a constrained Home Chat rail row.
 *
 * This is deliberately a projection, not something inferred from a title,
 * transcript, or client-side capability.  Workflow-only users must never see
 * a legacy/general-chat row simply because it happens to contain a familiar
 * card or binding shape.
 */
export type ConstrainedSessionKind = 'mr' | 'gpc' | 'construction' | 'workflow_choice' | 'purchase_guidance' | 'mission_partner_guidance';

export type ConstrainedSessionProjection = Readonly<{
  kind: ConstrainedSessionKind;
  /** Safe opaque binding summary. Its contents are never used to authorize. */
  binding?: Readonly<Record<string, string>>;
  /** Safe current action-choice card, when kind is workflow_choice. */
  chat_card?: Readonly<Record<string, unknown>>;
  /** Safe submitted entry copy for a constrained controller/card. */
  entry_message?: string;
}>;

const CONSTRAINED_SESSION_KINDS = new Set<ConstrainedSessionKind>([
  'mr', 'gpc', 'construction', 'workflow_choice', 'purchase_guidance', 'mission_partner_guidance',
]);

/** Reject malformed list metadata rather than expanding constrained history. */
export function normalizeConstrainedSessionProjection(
  raw: unknown,
): ConstrainedSessionProjection | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (
    typeof value.kind !== 'string'
    || !CONSTRAINED_SESSION_KINDS.has(value.kind as ConstrainedSessionKind)
  ) return null;
  const binding = value.binding;
  const safeBinding = binding && typeof binding === 'object' && !Array.isArray(binding)
    && Object.entries(binding).every(([key, item]) => key.trim().length > 0 && typeof item === 'string' && item.trim().length > 0)
    ? Object.fromEntries(Object.entries(binding).map(([key, item]) => [key, item as string]))
    : undefined;
  const card = value.chat_card;
  const isGuidance = value.kind === 'purchase_guidance' || value.kind === 'mission_partner_guidance';
  const guidanceCard = isGuidance && card !== undefined
    ? normalizeHomeChatCards([card]).find((item) => item.kind === value.kind)
    : null;
  if (isGuidance && card !== undefined && !guidanceCard) return null;
  const safeCard = guidanceCard || (!isGuidance
    && card && typeof card === 'object' && !Array.isArray(card)
    ? card as Record<string, unknown> : undefined);
  return {
    kind: value.kind as ConstrainedSessionKind,
    ...(safeBinding ? { binding: safeBinding } : {}),
    ...(safeCard ? { chat_card: safeCard } : {}),
    ...(typeof value.entry_message === 'string' && value.entry_message.trim()
      ? { entry_message: value.entry_message } : {}),
  };
}

/** Owner-authorized Services PWS chat binding; no client flow data is trusted. */
export type ServicesPwsChatBinding = {
  services_pws_chat_session_id: string;
  flow_id: string;
  bound_at: string;
};

/**
 * Server-owned, leased activity for a Home Chat session.
 *
 * This is deliberately metadata only. The browser uses it only to show that
 * a conversation is working; it never uses it to control a workflow or clear
 * an operation. A missing or malformed record is treated as inactive.
 */
export type SessionActivity = Readonly<{
  operation_id: string;
  /** Opaque client correlation for adopting a lazy-created ordinary turn. */
  client_request_id?: string;
  kind: string;
  label: string;
  started_at: string;
  expires_at: string;
}>;

/** Safe terminal-operation metadata projected by the owned session list. */
export type SessionCompletion = Readonly<{
  completion_id: string;
  operation_id: string;
  /** Only `completed` is a successful completion; other terminal states never notify. */
  state: 'completed' | 'cancelled' | 'failed';
  label: string;
  completed_at: string;
  unseen: boolean;
}>;

/**
 * A durable, owner-filtered notification that an LLM result is actionable in
 * the main Home Chat surface.  It is deliberately correlation metadata only:
 * the event never contains card/result content and is not a client-created
 * activity signal.
 */
export type HomeChatLlmResultFamily =
  | 'router_action_card'
  | 'gpc_result'
  | 'market_research_result'
  | 'construction_sow_result'
  | 'services_pws_result';

export type HomeChatLlmResultEvent = Readonly<{
  event_id: string;
  result_family: HomeChatLlmResultFamily;
  /** Safe, server-owned owner-action classification for event auditing. */
  actionable_subtype?: string;
  operation_id?: string;
  turn_id?: string;
  /** Server-owned branch revision; the server omits superseded branches. */
  branch_revision: number;
  created_at: string;
}>;

const HOME_CHAT_LLM_RESULT_FAMILIES = new Set<HomeChatLlmResultFamily>([
  'router_action_card',
  'gpc_result',
  'market_research_result',
  'construction_sow_result',
  'services_pws_result',
]);

/** Drop malformed/unrecognized additive event payloads rather than chime. */
export function normalizeHomeChatLlmResultEvents(raw: unknown): HomeChatLlmResultEvent[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): HomeChatLlmResultEvent[] => {
    if (!item || typeof item !== 'object') return [];
    const event = item as Record<string, unknown>;
    if (
      typeof event.event_id !== 'string' || !event.event_id.trim()
      || typeof event.result_family !== 'string'
      || !HOME_CHAT_LLM_RESULT_FAMILIES.has(event.result_family as HomeChatLlmResultFamily)
      || !Number.isSafeInteger(event.branch_revision) || (event.branch_revision as number) < 0
      || typeof event.created_at !== 'string' || !event.created_at.trim()
      || (event.operation_id !== undefined && (typeof event.operation_id !== 'string' || !event.operation_id.trim()))
      || (event.turn_id !== undefined && (typeof event.turn_id !== 'string' || !event.turn_id.trim()))
    ) return [];
    return [{
      event_id: event.event_id,
      result_family: event.result_family as HomeChatLlmResultFamily,
      ...(typeof event.actionable_subtype === 'string' && event.actionable_subtype.trim()
        ? { actionable_subtype: event.actionable_subtype } : {}),
      ...(typeof event.operation_id === 'string' ? { operation_id: event.operation_id } : {}),
      ...(typeof event.turn_id === 'string' ? { turn_id: event.turn_id } : {}),
      branch_revision: event.branch_revision as number,
      created_at: event.created_at,
    }];
  });
}

/** Reject malformed additive completion state rather than showing a false Ready marker. */
export function normalizeSessionCompletion(raw: unknown): SessionCompletion | null {
  if (!raw || typeof raw !== 'object') return null;
  const completion = raw as Record<string, unknown>;
  if (
    typeof completion.completion_id !== 'string' || !completion.completion_id.trim()
    || typeof completion.operation_id !== 'string' || !completion.operation_id.trim()
    || (completion.state !== 'completed' && completion.state !== 'cancelled' && completion.state !== 'failed')
    || typeof completion.label !== 'string' || !completion.label.trim()
    || typeof completion.completed_at !== 'string' || !completion.completed_at.trim()
    || typeof completion.unseen !== 'boolean'
  ) return null;
  return {
    completion_id: completion.completion_id,
    operation_id: completion.operation_id,
    state: completion.state,
    label: completion.label,
    completed_at: completion.completed_at,
    unseen: completion.unseen,
  };
}

/**
 * Parse the small, safe activity envelope exposed by the session-list API.
 *
 * The server remains the source of truth. Rejecting a malformed envelope is
 * preferable to leaving a misleading "Working" marker in the rail.
 */
export function normalizeSessionActivity(
  raw: unknown,
): SessionActivity | null {
  if (!raw || typeof raw !== 'object') return null;
  const activity = raw as Record<string, unknown>;
  const fields = [
    'operation_id',
    'kind',
    'label',
    'started_at',
    'expires_at',
  ] as const;
  if (
    fields.some(
      (field) => {
        const value = activity[field];
        return typeof value !== 'string' || !value.trim();
      },
    )
  ) {
    return null;
  }
  return {
    operation_id: activity.operation_id as string,
    ...(typeof activity.client_request_id === 'string' && activity.client_request_id.trim()
      ? { client_request_id: activity.client_request_id }
      : {}),
    kind: activity.kind as string,
    label: activity.label as string,
    started_at: activity.started_at as string,
    expires_at: activity.expires_at as string,
  };
}

export type ChatSession = {
  session_id: string;
  title: string | null;
  /** Server-authorized rename/delete capability for a constrained workflow chat. */
  can_manage?: boolean;
  created_at: string;
  last_activity_at: string;
  message_count: number;
  /** Present only while the server says this session has unexpired work. */
  activity?: SessionActivity | null;
  /** Safe, owner-filtered terminal state; omitted for sessions without a recent completion. */
  completion?: SessionCompletion | null;
  /** Active-branch, owner-filtered LLM results eligible for main-chat sound. */
  llm_result_events?: HomeChatLlmResultEvent[];
  /** Present when this Home Chat conversation is durably bound to GPC. */
  gpc_binding?: GpcChatBinding | null;
  /** Present when the thread is durably bound to Market Research chat. */
  mr_binding?: MrChatBinding | null;
  /** Server projection key for the owner-bound Construction SOW controller. */
  construction_sow_binding?: ConstructionSowChatBinding | null;
  services_pws_chat_binding?: ServicesPwsChatBinding | null;
  /** Present only when the server has structurally authorized this rail row. */
  constrained_session?: ConstrainedSessionProjection | null;
};

export type SessionListResponse = {
  sessions: ChatSession[];
  next_cursor: string | null;
};

/** A server-issued, opaque option for a pending type-selection card. */
export type SimilarRequirementsTypeOption = {
  label: string;
  selection_key: string;
};

/** Safe discovery metadata. Flow IDs, scores and document content stay server-side. */
export type SimilarRequirementsResultRow = {
  /** Stable opaque display identity, independent of permission to open. */
  result_key: string;
  can_open: boolean;
  read_only?: boolean;
  read_only_reason?: 'similar_requirements' | 'super_admin_view' | 'training' | null;
  workflow_name: string;
  workflow_type?: string | null;
  /** Approved display metadata; a Home Chat card never contains a flow ID. */
  owner_email?: string | null;
  owner_unit?: string | null;
  /** ISO timestamp used by the shared Similar Requirements table. */
  updated_at?: string | null;
  /** Backward-compatible preformatted timestamp for cards created before `updated_at`. */
  last_updated_display?: string | null;
  /** Present only when opening is allowed; fresh and never persisted by the browser. */
  open_handle?: string;
};

/** A safe placeholder for a deleted or otherwise unavailable result. */
export type SimilarRequirementsUnavailableRow = { status: 'unavailable' };

export type SimilarRequirementsTypeChooserCard = {
  kind: 'similar_requirements_type_chooser';
  card_id: string;
  /** Canonical durable position in the unified Home Chat conversation. */
  timeline_sequence: number;
  prompt?: string | null;
  state: 'pending' | 'selected' | 'cancelled' | 'error';
  options: SimilarRequirementsTypeOption[];
  /** User-safe server message only; never technical detail. */
  error?: string | null;
};

export type SimilarRequirementsResultCard = {
  kind: 'similar_requirements_results';
  card_id: string;
  /** Canonical durable position in the unified Home Chat conversation. */
  timeline_sequence: number;
  searched_type_label: string;
  state: 'ready' | 'complete' | 'error';
  results: Array<SimilarRequirementsResultRow | SimilarRequirementsUnavailableRow>;
  /** User-safe server message only; never technical detail. */
  error?: string | null;
};

export type SimilarRequirementsClarificationCard = {
  kind: 'similar_requirements_clarification';
  card_id: string;
  /** Canonical durable position in the unified Home Chat conversation. */
  timeline_sequence: number;
  prompt: string;
  state?: 'ready' | 'complete' | 'cancelled' | 'error';
};

type AmbiguousIntentChoiceOption = SimilarRequirementsTypeOption;

/**
 * The browser recognizes only these two server-owned intent-choice contracts.
 * The option labels are action keys for rendering purposes only; eligibility
 * and the mapping from a selection key to an action stay on the server.
 */
export type AmbiguousIntentChoiceOptions =
  | readonly [AmbiguousIntentChoiceOption, AmbiguousIntentChoiceOption]
  | readonly [AmbiguousIntentChoiceOption, AmbiguousIntentChoiceOption, AmbiguousIntentChoiceOption]
  | readonly [AmbiguousIntentChoiceOption, AmbiguousIntentChoiceOption, AmbiguousIntentChoiceOption, AmbiguousIntentChoiceOption];

/** Persisted top-level clarification when multiple Home Chat actions overlap. */
export type AmbiguousIntentChoiceCard = {
  kind: 'ambiguous_intent_choice';
  card_id: string;
  timeline_sequence: number;
  state: 'pending' | 'selected' | 'cancelled' | 'error';
  prompt: string;
  /** One of the two exact server-issued, ordered action contracts. */
  options: AmbiguousIntentChoiceOptions;
  error?: string | null;
};

/** A server-issued workflow path. The browser never derives this from a label. */
export type WorkflowRecommendationType =
  | 'market_research'
  | 'clause_logic'
  | 'sow_verification'
  | 'wage_determination'
  | 'pnm'
  | 'fopr'
  | 'services'
  | 'one_time_construction'
  | 'services_combo'
  | 'gpc'
  | 'complex_services'
  | 'post_award_actions'
  | 'other_transactions';

/**
 * A bounded, server-issued variation of an otherwise ordinary workflow
 * handoff. It is a presentation instruction for the app shell only; it never
 * grants access or identifies a workflow record.
 */
export type WorkflowRecommendationEntryMode = 'standalone_price_estimator';

/**
 * Identifies the durable Home Chat conversation that authorized a workflow
 * handoff. App carries this exact session into the workflow-side assistant;
 * the browser never creates a replacement conversation for the destination.
 */
export type HomeChatHandoffContext = Readonly<{
  homeChatSessionId: string;
  /** Server-issued proof that this session authorized a generic workflow. */
  workflowHandoffReceipt?: string;
  /** A bounded server-issued entry variation for the selected workflow. */
  workflowEntryMode?: WorkflowRecommendationEntryMode;
}>;

/**
 * The workflow page currently surrounding an embedded Home Chat panel.
 * This is conversational context only: it never grants access or identifies
 * an authoritative workflow record.
 */
export type HomeChatPageContext = Readonly<{
  workflowType: string;
  workflowLabel: string;
  workflowName?: string;
  stageName: string;
  stageDescription?: string;
  flowId?: string;
  stageNumber?: number;
}>;

export type WorkflowRecommendationOption = {
  label: string;
  description: string;
  selection_key: string;
};

/** Derived by the server from an actual completed workflow creation. */
export type CreatedHomeChatWorkflow =
  | { status: 'available'; flow_id: string }
  | { status: 'unavailable' };

export function normalizeCreatedWorkflow(raw: unknown): CreatedHomeChatWorkflow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (value.status === 'unavailable' && Object.keys(value).length === 1) return { status: 'unavailable' };
  if (value.status === 'available' && Object.keys(value).length === 2
    && typeof value.flow_id === 'string' && value.flow_id.trim()) {
    return { status: 'available', flow_id: value.flow_id };
  }
  return null;
}

/** A distinct, server-owned card contract for launching an existing workflow flow. */
export type WorkflowRecommendationChoiceCard = {
  kind: 'workflow_recommendation_choice';
  card_id: string;
  timeline_sequence: number;
  state: 'pending' | 'selected' | 'error';
  prompt: string;
  options: WorkflowRecommendationOption[];
  /**
   * Safe, server-authored selection receipt for a completed recommendation.
   * It is display-only; the browser never derives a workflow type or flow ID
   * from it. Older selected cards may omit it, in which case the UI shows a
   * generic opened-workflow receipt.
   */
  selected_label?: string;
  created_workflow?: CreatedHomeChatWorkflow;
  error?: string | null;
};

/** Generic server-owned choice; keys are opaque and never encode an action. */
export type ActionChoiceOption = {
  label: string;
  description?: string;
  selection_key: string;
  /** Server-owned rendering hint; it never identifies the private action. */
  presentation?: 'back';
  /** Server-authored behavior only; the private action and workflow stay opaque. */
  requires_workflow_navigation?: true;
};

export type ActionChoiceCard = {
  kind: 'action_choice';
  card_id: string;
  timeline_sequence: number;
  state: 'pending' | 'selected' | 'error';
  prompt: string;
  options: ActionChoiceOption[];
  selected_label?: string;
  created_workflow?: CreatedHomeChatWorkflow;
  error?: string | null;
};

/** Server-issued action attached to one completed ordinary answer. */
export type TrainingActionCard = {
  kind: 'training_action';
  card_id: string;
  timeline_sequence: number;
  state: 'pending' | 'error';
  prompt: 'Open Training';
  error?: string | null;
};

/** Server-authored purchase routing; only opaque option keys may be relayed. */
export type PurchaseGuidanceSourceCheck = {
  source_id: '2git';
  item_id: string;
  item_label: string;
  status: 'searching' | 'matches' | 'no_matches' | 'check_unavailable';
  summary: string;
  official_url: string;
  matches: Array<{ label: string; url: string }>;
  checked_at?: string;
};

export type PurchaseGuidanceCard = {
  kind: 'purchase_guidance';
  card_id: string;
  timeline_sequence: number;
  state: 'ready' | 'checking' | 'hold' | 'support' | 'pending' | 'selected' | 'error';
  variant: 'catalog' | 'vehicle' | 'clarification' | 'prohibited' | 'controlled_source';
  /** A single assistant-facing question for a typed clarification turn. */
  message?: string;
  bluf: string;
  route_label: string;
  program_name?: string;
  matched_family?: string;
  reason: string;
  steps: Array<{ id: string; text: string }>;
  policy_basis: {
    policy_id: string;
    version: string;
    owner: string;
    effective_at: string;
    expires_at?: string;
    last_verified_at: string;
    links: Array<{ label: string; url: string }>;
  };
  disclosure?: string;
  warning?: string;
  support_text?: string;
  pending_text?: string;
  receipt?: string;
  options: Array<{ label: string; selection_key: string; description?: string }>;
  source_checks?: PurchaseGuidanceSourceCheck[];
};

export type MissionPartnerGuidanceCard = {
  kind: 'mission_partner_guidance';
  card_id: string;
  timeline_sequence: number;
  state: 'ready' | 'support' | 'selected' | 'error';
  scenario_id: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'L6' | 'L7' | 'L8' | null;
  content_version: string;
  perspective: 'customer' | 'contracting' | 'neutral';
  focus: 'overview' | 'forms' | 'contacts' | 'actions' | 'workflow' | 'authority';
  title: string;
  bluf: string;
  steps: Array<{ id: string; text: string; workflow_action_ids?: string[]; contact_role_ids?: string[] }>;
  references: Array<{ label: string; url: string }>;
  contact_role_ids: string[];
  /** Current actor-authorized projection only; never a stored transcript directory. */
  contact_directory?: RaContacts | null;
  /** Authenticated actor's unit for contact feedback, not directory access. */
  contact_unit?: string | null;
  options: Array<{ selection_key: string; label: string; workflow_type: 'market_research' | 'gpc' | 'one_time_construction'; entry_mode?: 'standalone_price_estimator'; step_id?: string }>;
  clarification_question?: string;
};

export type HomeChatGuidanceCard = PurchaseGuidanceCard | MissionPartnerGuidanceCard;

export type HomeChatCard =
  | SimilarRequirementsTypeChooserCard
  | SimilarRequirementsResultCard
  | SimilarRequirementsClarificationCard
  | AmbiguousIntentChoiceCard
  | WorkflowRecommendationChoiceCard
  | ActionChoiceCard
  | TrainingActionCard
  | PurchaseGuidanceCard
  | MissionPartnerGuidanceCard;

const hasOnlyFields = (value: Record<string, unknown>, fields: readonly string[]) =>
  Object.keys(value).every((key) => fields.includes(key));

const boundedText = (value: unknown, limit: number) =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= limit;

const officialGsaUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.length > 2048 || /\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password
      && ['gsaadvantage.gov', 'www.gsaadvantage.gov'].includes(url.hostname);
  } catch { return false; }
};

function normalizeSourceChecks(raw: unknown): PurchaseGuidanceSourceCheck[] | null {
  if (!Array.isArray(raw) || raw.length > 2) return null;
  const checks: PurchaseGuidanceSourceCheck[] = [];
  for (const rawCheck of raw) {
    if (!rawCheck || typeof rawCheck !== 'object' || Array.isArray(rawCheck)) return null;
    const check = rawCheck as Record<string, unknown>;
    if (!hasOnlyFields(check, ['source_id', 'item_id', 'item_label', 'status', 'summary',
      'official_url', 'matches', 'checked_at']) || check.source_id !== '2git'
      || typeof check.item_id !== 'string' || !/^chat-[0-9a-f]{16}$/.test(check.item_id)
      || !boundedText(check.item_label, 80) || !boundedText(check.summary, 240)
      || !officialGsaUrl(check.official_url)
      || !['searching', 'matches', 'no_matches', 'check_unavailable'].includes(check.status as string)
      || !Array.isArray(check.matches) || check.matches.length > 3
      || (check.status !== 'matches' && check.matches.length > 0)
      || (check.checked_at !== undefined && (
        typeof check.checked_at !== 'string' || check.checked_at.length > 40
        || Number.isNaN(Date.parse(check.checked_at))
      ))) return null;
    const matches: Array<{ label: string; url: string }> = [];
    for (const rawMatch of check.matches) {
      if (!rawMatch || typeof rawMatch !== 'object' || Array.isArray(rawMatch)) return null;
      const match = rawMatch as Record<string, unknown>;
      if (!hasOnlyFields(match, ['label', 'url']) || !boundedText(match.label, 160)
        || !officialGsaUrl(match.url)) return null;
      matches.push({ label: match.label as string, url: match.url });
    }
    checks.push({ source_id: '2git', item_id: check.item_id, item_label: check.item_label as string,
      status: check.status as PurchaseGuidanceSourceCheck['status'], summary: check.summary as string,
      official_url: check.official_url, matches,
      ...(typeof check.checked_at === 'string' ? { checked_at: check.checked_at } : {}),
    });
  }
  return new Set(checks.map(check => check.item_id)).size === checks.length ? checks : null;
}

function normalizePurchaseGuidanceCard(value: Record<string, unknown>): PurchaseGuidanceCard | null {
  if (!hasOnlyFields(value, [
    'kind', 'card_id', 'timeline_sequence', 'order', 'state', 'variant', 'bluf', 'route_label',
    'program_name', 'matched_family', 'reason', 'steps', 'policy_basis', 'disclosure', 'message',
    'warning', 'support_text', 'pending_text', 'receipt', 'options', 'source_checks', 'created_at', 'updated_at',
  ])) return null;
  const state = value.state;
  const variant = value.variant;
  if (!['ready', 'checking', 'hold', 'support', 'pending', 'selected', 'error'].includes(state as string)
    || !['catalog', 'vehicle', 'clarification', 'prohibited', 'controlled_source'].includes(variant as string)
    || typeof value.card_id !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(value.card_id)
    || !Number.isSafeInteger(value.timeline_sequence) || (value.timeline_sequence as number) < 0
    || !boundedText(value.bluf, 1200) || !boundedText(value.route_label, 1200)
    || !boundedText(value.reason, 1200)) return null;
  for (const key of ['program_name', 'matched_family', 'disclosure', 'message', 'warning', 'support_text', 'pending_text', 'receipt']) {
    if (value[key] !== undefined && !boundedText(value[key], 1200)) return null;
  }
  if (value.message !== undefined && (variant !== 'clarification' || state !== 'support')) return null;
  if ((state === 'selected') !== (value.receipt !== undefined)) return null;
  if ((value.created_at !== undefined && typeof value.created_at !== 'string')
    || (value.updated_at !== undefined && typeof value.updated_at !== 'string')
    || (value.order !== undefined && (!Number.isSafeInteger(value.order) || (value.order as number) < 0))) return null;
  if (!Array.isArray(value.steps) || value.steps.length > 12) return null;
  const steps = value.steps as Array<Record<string, unknown>>;
  if (steps.some((step) => !step || typeof step !== 'object' || Array.isArray(step)
    || !hasOnlyFields(step, ['id', 'text']) || Object.keys(step).length !== 2
    || typeof step.id !== 'string' || !/^[a-z][a-z0-9_]{0,63}$/.test(step.id)
    || !boundedText(step.text, 1600))
    || new Set(steps.map((step) => step.id)).size !== steps.length) return null;
  const basis = value.policy_basis;
  if (!basis || typeof basis !== 'object' || Array.isArray(basis)) return null;
  const policy = basis as Record<string, unknown>;
  if (!hasOnlyFields(policy, ['policy_id', 'version', 'owner', 'effective_at', 'expires_at', 'last_verified_at', 'links'])
    || ['policy_id', 'version', 'owner', 'effective_at', 'last_verified_at'].some((key) => !boundedText(policy[key], 240))
    || (policy.expires_at !== undefined && !boundedText(policy.expires_at, 240))
    || !Array.isArray(policy.links) || policy.links.length > 12) return null;
  const links = policy.links as Array<Record<string, unknown>>;
  if (links.some((link) => {
    if (!link || typeof link !== 'object' || Array.isArray(link)
      || !hasOnlyFields(link, ['label', 'url']) || Object.keys(link).length !== 2
      || !boundedText(link.label, 240) || !boundedText(link.url, 2048)) return true;
    try {
      const url = new URL(link.url as string);
      return url.protocol !== 'https:' || !url.hostname || !!url.username || !!url.password
        || /\s/.test(link.url as string);
    } catch { return true; }
  })) return null;
  if (!Array.isArray(value.options) || value.options.length > 4) return null;
  const options = value.options as Array<Record<string, unknown>>;
  if (options.some((option) => !option || typeof option !== 'object' || Array.isArray(option)
    || !hasOnlyFields(option, ['label', 'selection_key', 'description'])
    || !boundedText(option.label, 240) || !boundedText(option.selection_key, 128)
    || (option.description !== undefined && !boundedText(option.description, 600))
    || !/^[A-Za-z0-9_-]{1,128}$/.test(option.selection_key as string)
    || (/gpc|purchase card/i.test(option.label as string) && option.label !== 'Start GPC'))
    || new Set(options.map((option) => option.selection_key)).size !== options.length) return null;
  if (state === 'ready' && !options.length) return null;
  if (['checking', 'hold', 'support', 'error', 'selected'].includes(state as string) && options.length) return null;
  const sourceChecks = value.source_checks === undefined ? [] : normalizeSourceChecks(value.source_checks);
  if (sourceChecks === null) return null;
  // Build a fresh projection so persistence metadata and prototypes never reach the view.
  return {
    kind: 'purchase_guidance', card_id: value.card_id,
    timeline_sequence: value.timeline_sequence as number,
    state: state as PurchaseGuidanceCard['state'], variant: variant as PurchaseGuidanceCard['variant'],
    bluf: value.bluf as string, route_label: value.route_label as string,
    ...(typeof value.program_name === 'string' ? { program_name: value.program_name } : {}),
    ...(typeof value.matched_family === 'string' ? { matched_family: value.matched_family } : {}),
    reason: value.reason as string,
    steps: steps.map((step) => ({ id: step.id as string, text: step.text as string })),
    policy_basis: {
      policy_id: policy.policy_id as string, version: policy.version as string,
      owner: policy.owner as string, effective_at: policy.effective_at as string,
      ...(typeof policy.expires_at === 'string' ? { expires_at: policy.expires_at } : {}),
      last_verified_at: policy.last_verified_at as string,
      links: links.map((link) => ({ label: link.label as string, url: link.url as string })),
    },
    ...Object.fromEntries(['disclosure', 'message', 'warning', 'support_text', 'pending_text', 'receipt']
      .filter((key) => typeof value[key] === 'string').map((key) => [key, value[key]])),
    options: options.map((option) => ({
      label: option.label as string, selection_key: option.selection_key as string,
      ...(typeof option.description === 'string' ? { description: option.description } : {}),
    })),
    ...(value.source_checks !== undefined ? { source_checks: sourceChecks } : {}),
  } as PurchaseGuidanceCard;
}

export function isCurrentPurchaseGuidanceSelection(card: PurchaseGuidanceCard, selectionKey: string): boolean {
  return card.state === 'ready' && card.options.some((option) => option.selection_key === selectionKey);
}

/** Opaque server-issued navigation instruction; it is never a workflow ID. */
export type HomeChatNavigationInstruction = {
  navigation_handle: string;
};

/** A direct-turn response that carries one safe, persisted Home Chat card. */
export type HomeChatCardResponse = {
  session_id?: string;
  chat_card: HomeChatCard;
};

const GPC_CAPABLE_INTENT_LABELS = [
  'Start a GPC purchase',
  'Search similar requirements',
  'Get procurement guidance',
] as const;

const NO_GPC_INTENT_LABELS = [
  'Search similar requirements',
  'Get procurement guidance',
] as const;

const WORKFLOW_CAPABLE_INTENT_LABELS = [
  'Research products and suppliers — Commodities Market Research',
  'Buy with a purchase card — GPC',
  'Find similar past requirements',
  'Get procurement guidance',
] as const;

function normalizeAmbiguousIntentChoiceOptions(raw: unknown): AmbiguousIntentChoiceOptions | null {
  if (!Array.isArray(raw)) return null;
  const options = raw.flatMap((option): SimilarRequirementsTypeOption[] => (
    option
      && typeof option === 'object'
      && typeof (option as Record<string, unknown>).label === 'string'
      && typeof (option as Record<string, unknown>).selection_key === 'string'
      && (option as Record<string, string>).selection_key.trim().length > 0
      ? [{
        label: (option as Record<string, string>).label,
        selection_key: (option as Record<string, string>).selection_key,
      }]
      : []
  ));
  // A malformed entry must invalidate the entire server card, rather than
  // silently reducing an action list into a different valid-looking contract.
  if (options.length !== raw.length) return null;
  const labels = options.map((option) => option.label);
  const expected = labels.length === NO_GPC_INTENT_LABELS.length
    ? NO_GPC_INTENT_LABELS
    : labels.length === WORKFLOW_CAPABLE_INTENT_LABELS.length
      ? WORKFLOW_CAPABLE_INTENT_LABELS
    : labels.length === GPC_CAPABLE_INTENT_LABELS.length
      ? GPC_CAPABLE_INTENT_LABELS
      : null;
  if (
    !expected
    || labels.some((label, index) => label !== expected[index])
    || new Set(options.map((option) => option.selection_key)).size !== options.length
  ) return null;
  return options as unknown as AmbiguousIntentChoiceOptions;
}

/** A selection can only relay a key from the currently normalized card. */
export function isCurrentAmbiguousIntentChoiceSelection(
  card: AmbiguousIntentChoiceCard,
  selectionKey: string
): boolean {
  return card.state === 'pending'
    && card.options.some((option) => option.selection_key === selectionKey);
}

/** A recommendation key can only be relayed from its current, normalized card. */
export function isCurrentWorkflowRecommendationSelection(
  card: WorkflowRecommendationChoiceCard,
  selectionKey: string
): boolean {
  return !card.created_workflow
    && card.options.some((option) => option.selection_key === selectionKey
      && (card.state === 'pending' || (card.state === 'selected' && option.label === card.selected_label)));
}

function normalizeWorkflowRecommendationOptions(raw: unknown, allowEmpty: boolean): WorkflowRecommendationOption[] | null {
  if (!Array.isArray(raw) || (!allowEmpty && raw.length === 0)) return null;
  const options = raw.flatMap((option): WorkflowRecommendationOption[] => {
    if (!option || typeof option !== 'object') return [];
    const value = option as Record<string, unknown>;
    const allowedFields = new Set(['label', 'description', 'selection_key']);
    if (Object.keys(value).some((key) => !allowedFields.has(key))) return [];
    if (
      typeof value.label !== 'string' || !value.label.trim()
      || typeof value.description !== 'string' || !value.description.trim()
      || typeof value.selection_key !== 'string' || !value.selection_key.trim()
    ) return [];
    return [{ label: value.label, description: value.description, selection_key: value.selection_key }];
  });
  if (options.length !== raw.length) return null;
  if (new Set(options.map((option) => option.selection_key)).size !== options.length) return null;
  return options;
}

/**
 * Normalize only the safe, server-owned canonical card envelope, deliberately
 * discarding every unknown field before it reaches a rendering component.
 */
export function normalizeHomeChatCards(raw: unknown): HomeChatCard[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((value): HomeChatCard[] => {
    if (!value || typeof value !== 'object') return [];
    const card = value as Record<string, unknown>;
    const timelineSequence = Number.isSafeInteger(card.timeline_sequence) && (card.timeline_sequence as number) >= 0
      ? card.timeline_sequence as number
      // Threads persisted before the unified timeline contract used a
      // card-only `order`. Keep those cards visible, but never emit `order`
      // from the normalized current contract.
      : Number.isSafeInteger(card.order) && (card.order as number) >= 0
        ? card.order as number
        : null;
    if (
      typeof card.card_id !== 'string' ||
      typeof card.kind !== 'string' ||
      timelineSequence === null
    ) return [];
    const state = typeof card.state === 'string' ? card.state : '';
    if (card.kind === 'mission_partner_guidance') {
      const guidance = normalizeMissionPartnerGuidanceCard({ ...card, timeline_sequence: timelineSequence });
      return guidance ? [guidance] : [];
    }
    if (card.kind === 'purchase_guidance') {
      const guidance = normalizePurchaseGuidanceCard(card);
      return guidance ? [guidance] : [];
    }
    if (card.kind === 'similar_requirements_type_chooser') {
      const options = Array.isArray(card.options)
        ? card.options.flatMap((option): SimilarRequirementsTypeOption[] => (
          option && typeof option === 'object' && typeof (option as Record<string, unknown>).label === 'string' && typeof (option as Record<string, unknown>).selection_key === 'string'
            ? [{ label: (option as Record<string, string>).label, selection_key: (option as Record<string, string>).selection_key }]
            : []
        ))
        : [];
      return [{
        kind: 'similar_requirements_type_chooser',
        card_id: card.card_id,
        timeline_sequence: timelineSequence,
        state: state === 'cancelled' || state === 'selected' || state === 'error' ? state : 'pending',
        prompt: typeof card.prompt === 'string' ? card.prompt : null,
        options,
        error: typeof card.error === 'string' ? card.error : null,
      }];
    }
    if (card.kind === 'similar_requirements_results') {
      const rows = Array.isArray(card.results) ? card.results.flatMap((row): Array<SimilarRequirementsResultRow | SimilarRequirementsUnavailableRow> => {
        if (!row || typeof row !== 'object') return [];
        const result = row as Record<string, unknown>;
        // A generic unavailable row deliberately has no handle and cannot open.
        if (result.status === 'unavailable') return [{ status: 'unavailable' }];
        const openHandle = typeof result.open_handle === 'string' && result.open_handle.trim()
          ? result.open_handle : undefined;
        // Old hydrated cards have only a handle; new foreign summaries have a
        // stable opaque key and deliberately have no open capability.
        const resultKey = typeof result.result_key === 'string' && result.result_key.trim()
          ? result.result_key : openHandle;
        if (!resultKey || typeof result.workflow_name !== 'string' || !result.workflow_name) return [];
        const canOpen = !!openHandle && (result.can_open === true || result.can_open === undefined);
        return [{
          result_key: resultKey,
          can_open: canOpen,
          read_only: result.read_only === true,
          read_only_reason: result.read_only_reason === 'similar_requirements' || result.read_only_reason === 'super_admin_view' || result.read_only_reason === 'training'
            ? result.read_only_reason : null,
          workflow_name: result.workflow_name,
          workflow_type: typeof result.workflow_type === 'string' ? result.workflow_type : null,
          owner_email: typeof result.owner_email === 'string' ? result.owner_email : null,
          owner_unit: typeof result.owner_unit === 'string' ? result.owner_unit : null,
          updated_at: typeof result.updated_at === 'string' ? result.updated_at : null,
          last_updated_display: typeof result.last_updated_display === 'string' ? result.last_updated_display : null,
          ...(canOpen ? { open_handle: openHandle } : {}),
        }];
      }) : [];
      return [{
        kind: 'similar_requirements_results',
        card_id: card.card_id,
        timeline_sequence: timelineSequence,
        searched_type_label: typeof card.searched_type_label === 'string' ? card.searched_type_label : 'workflow type',
        state: state === 'error' ? 'error' : state === 'ready' ? 'ready' : 'complete',
        results: rows,
        error: typeof card.error === 'string' ? card.error : null,
      }];
    }
    if (card.kind === 'similar_requirements_clarification') {
      return [{
        kind: 'similar_requirements_clarification',
        card_id: card.card_id,
        timeline_sequence: timelineSequence,
        prompt: typeof card.prompt === 'string' ? card.prompt : 'Please rephrase what you need or provide a little more detail.',
        state: state === 'cancelled' || state === 'error' || state === 'complete' ? state : 'ready',
      }];
    }
    if (card.kind === 'ambiguous_intent_choice') {
      const options = normalizeAmbiguousIntentChoiceOptions(card.options);
      if (!options) return [];
      return [{
        kind: 'ambiguous_intent_choice',
        card_id: card.card_id,
        timeline_sequence: timelineSequence,
        state: state === 'selected' || state === 'cancelled' || state === 'error' ? state : 'pending',
        prompt: typeof card.prompt === 'string' ? card.prompt : 'How would you like to proceed?',
        options,
        error: typeof card.error === 'string' ? card.error : null,
      }];
    }
    if (card.kind === 'workflow_recommendation_choice') {
      // Keep private dispatch mappings out of the rendering contract. The
      // server may add a verified destination only after actual creation.
      const allowedFields = new Set([
        'kind', 'card_id', 'timeline_sequence', 'order', 'state', 'prompt', 'options', 'selected_label', 'error', 'created_workflow',
        // Storage timestamps are safe persistence metadata. They remain
        // deliberately absent from the normalized rendering contract.
        'created_at', 'updated_at',
      ]);
      if (Object.keys(card).some((key) => !allowedFields.has(key))) return [];
      if (
        (card.created_at !== undefined && typeof card.created_at !== 'string')
        || (card.updated_at !== undefined && typeof card.updated_at !== 'string')
      ) return [];
      const options = normalizeWorkflowRecommendationOptions(card.options, state !== 'pending');
      if (!card.card_id.trim() || !options || typeof card.prompt !== 'string' || !card.prompt.trim()) return [];
      if (state !== 'pending' && state !== 'selected' && state !== 'error') return [];
      if (
        (card.selected_label !== undefined && (state !== 'selected' || typeof card.selected_label !== 'string' || !card.selected_label.trim()))
      ) return [];
      if (state === 'error' && typeof card.error !== 'string') return [];
      const createdWorkflow = card.created_workflow === undefined ? undefined : normalizeCreatedWorkflow(card.created_workflow);
      if (createdWorkflow === null) return [];
      return [{
        kind: 'workflow_recommendation_choice',
        card_id: card.card_id,
        timeline_sequence: timelineSequence,
        state,
        prompt: card.prompt,
        options,
        ...(typeof card.selected_label === 'string' ? { selected_label: card.selected_label } : {}),
        ...(createdWorkflow ? { created_workflow: createdWorkflow } : {}),
        error: typeof card.error === 'string' ? card.error : null,
      }];
    }
    if (card.kind === 'action_choice') {
      const allowedFields = new Set([
        'kind', 'card_id', 'timeline_sequence', 'order', 'state', 'prompt', 'options', 'selected_label', 'error', 'created_at', 'updated_at', 'created_workflow',
      ]);
      if (Object.keys(card).some((key) => !allowedFields.has(key))) return [];
      if ((card.created_at !== undefined && typeof card.created_at !== 'string') || (card.updated_at !== undefined && typeof card.updated_at !== 'string')) return [];
      if (!card.card_id.trim() || typeof card.prompt !== 'string' || !card.prompt.trim()) return [];
      if (state !== 'pending' && state !== 'selected' && state !== 'error') return [];
      const options = Array.isArray(card.options) ? card.options.flatMap((rawOption): ActionChoiceOption[] => {
        if (!rawOption || typeof rawOption !== 'object') return [];
        const option = rawOption as Record<string, unknown>;
        if (Object.keys(option).some((key) => ![
          'label', 'description', 'selection_key', 'presentation', 'requires_workflow_navigation',
        ].includes(key))) return [];
        if (typeof option.label !== 'string' || !option.label.trim() || typeof option.selection_key !== 'string' || !option.selection_key.trim()) return [];
        if (option.description !== undefined && (typeof option.description !== 'string' || !option.description.trim())) return [];
        if (option.presentation !== undefined && option.presentation !== 'back') return [];
        if (option.requires_workflow_navigation !== undefined && option.requires_workflow_navigation !== true) return [];
        return [{
          label: option.label,
          selection_key: option.selection_key,
          ...(typeof option.description === 'string' ? { description: option.description } : {}),
          ...(option.presentation === 'back' ? { presentation: 'back' as const } : {}),
          ...(option.requires_workflow_navigation === true
            ? { requires_workflow_navigation: true as const }
            : {}),
        }];
      }) : [];
      if (options.length !== (Array.isArray(card.options) ? card.options.length : -1) || new Set(options.map((option) => option.selection_key)).size !== options.length) return [];
      // A completed action retains its original opaque options as durable
      // conversation choices. The browser can later relay an original opaque
      // key, but never invent an action from labels or local metadata.
      if (state === 'pending' && !options.length) return [];
      if (card.selected_label !== undefined && (state !== 'selected' || typeof card.selected_label !== 'string' || !card.selected_label.trim())) return [];
      if (state === 'error' && typeof card.error !== 'string') return [];
      const createdWorkflow = card.created_workflow === undefined ? undefined : normalizeCreatedWorkflow(card.created_workflow);
      if (createdWorkflow === null) return [];
      return [{
        kind: 'action_choice', card_id: card.card_id, timeline_sequence: timelineSequence, state, prompt: card.prompt, options,
        ...(typeof card.selected_label === 'string' ? { selected_label: card.selected_label } : {}),
        ...(createdWorkflow ? { created_workflow: createdWorkflow } : {}),
        error: typeof card.error === 'string' ? card.error : null,
      }];
    }
    if (card.kind === 'training_action') {
      const allowedFields = new Set([
        'kind', 'card_id', 'timeline_sequence', 'state', 'prompt', 'error', 'created_at',
      ]);
      if (Object.keys(card).some((key) => !allowedFields.has(key))) return [];
      if (!card.card_id.trim() || card.prompt !== 'Open Training') return [];
      if (state !== 'pending' && state !== 'error') return [];
      return [{
        kind: 'training_action', card_id: card.card_id,
        timeline_sequence: timelineSequence, state,
        prompt: 'Open Training',
        error: typeof card.error === 'string' ? card.error : null,
      }];
    }
    return [];
  });
}

export function isCurrentActionChoiceSelection(card: ActionChoiceCard, selectionKey: string): boolean {
  return !card.created_workflow && (card.state === 'pending' || card.state === 'selected' || card.state === 'error')
    && card.options.some((option) => option.selection_key === selectionKey);
}

export type WorkflowDocumentHistory = {
  flow_id: string;
  home_chat_session_id: string;
  document_kind: 'construction_review' | 'construction_generated' | 'services_review' | 'services_generated';
  document_label: string;
  history: import('../../api/manualEditAi').ManualEditAiHistory;
};

export type HomeThreadResponse = {
  workflow_document_histories?: WorkflowDocumentHistory[];
  workflow_document_history_error?: string;
  session_id: string;
  owner_email: string;
  title: string | null;
  // Re-imported here as a value-position type reference. Using `any[]`
  // would erase information; this `import('./types').ChatMessage[]` form
  // keeps the type self-referential without a circular runtime import.
  messages: import('../FarChat/types').ChatMessage[];
  attachments: string[];
  /**
   * v2 chat turns. Optional for back-compat with sessions that only have
   * legacy v1 ``messages``. The wire shape matches the POST /v2/turn
   * response (``response.{summary,detail,citations}`` envelope) — callers
   * should normalize each entry via :func:`normalizeV2Turn` before
   * stuffing them into UI state.
   */
  turns?: Array<{
    turn_id: string;
    user_msg: string;
    response: {
      summary: string;
      detail: string;
      citations: Array<import('../FarChat/types').Citation | string>;
    };
    reader_outputs?: unknown;
    is_refusal?: boolean;
    refusal_text?: string;
    timestamp?: string;
    /** Canonical durable position in the unified Home Chat transcript. */
    timeline_sequence?: number;
    /** Legacy compatibility fallback; new server responses use timeline_sequence. */
    order?: number;
    /** Chosen clarified question, or null/absent (legacy). */
    clarification?: string | null;
    /** Chosen post-reader topic focus, or null/absent (legacy). */
    topic_focus?: string | null;
    editable?: boolean;
  }>;
  /** Current server-owned active transcript branch revision for replacement CAS. */
  branch_revision?: number;
  /** Owner-filtered durable result-event metadata for main Home Chat only. */
  llm_result_events?: HomeChatLlmResultEvent[];
  /** Ordered typed card events; absent on pre-card threads. */
  chat_cards?: unknown;
  created_at: string | null;
  last_activity_at: string | null;
  message_count: number;
  /** Owner-authorized binding metadata used to identify a GPC-backed chat. */
  gpc_binding?: GpcChatBinding | null;
  mr_binding?: MrChatBinding | null;
  /** Server projection key for the owner-bound Construction SOW controller. */
  construction_sow_binding?: ConstructionSowChatBinding | null;
  services_pws_chat_binding?: ServicesPwsChatBinding | null;
  constrained_session?: ConstrainedSessionProjection | null;
  /** Authoritative persisted controller view, supplied only for the owner. */
  gpc_chat?: import('../GpcChat/types').GpcChatSessionView | null;
  mr_chat?: import('../MrChat/types').MrChatSessionView | null;
  /** Reloadable server-issued confirmation for an unbound GPC handoff. */
  gpc_pending_confirmation?: unknown;
  construction_sow_chat?: import('../ConstructionSowChat/types').ConstructionSowChatSessionView | null;
  services_pws_chat?: import('../ServicesPwsChat/types').ServicesPwsChatSessionView | null;
  /**
   * Mid-flight v2 turn marker (mirrors FarChat ThreadEnvelope). Non-null
   * when a turn POST is currently dispatching server-side; cleared on
   * completion / failure. The panel uses this on session mount to
   * rehydrate the `StepProgressChecklist` after a reload.
   */
  pending_request?: import('../FarChat/types').PendingRequest | null;
  /**
   * Persisted pre-answer FAR-version clarification. Present when the user
   * closes a session before selecting a regime/answer mode; the panel
   * restores the card from this server-owned envelope on reopen.
   */
  pending_clarification?: import('../FarChat/types').ClarificationEnvelope | null;
};
